import { useEffect, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";

import { serpPlaygroundUrl } from "../../models/SerpQuery/SerpQuery";

interface SerpQueryPreviewProps {
  title: string;
  /** The query string with {{tag}} where a value is filled in per search. */
  query: string;
  hint?: string;
}

/** Wraps each {{tag}} so it stands out from the fixed parts of the query. */
function highlightTags(query: string) {
  return query.split(/(\{\{[^}]+\}\})/g).map((part, index) =>
    /^\{\{[^}]+\}\}$/.test(part) ? (
      <span key={index} className="rounded bg-orange-100 px-0.5 text-orange-800">
        {part}
      </span>
    ) : (
      <span key={index}>{part}</span>
    ),
  );
}

/** The query SerpApi receives, ready to copy or open in the SerpApi playground. */
export default function SerpQueryPreview({ title, query, hint }: SerpQueryPreviewProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    navigator.clipboard?.writeText(query).then(() => setCopied(true));
  };

  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            {copied ? "Copied" : "Copy"}
          </button>
          <a
            href={serpPlaygroundUrl(query)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Open in SerpApi
          </a>
        </div>
      </div>
      <p className="mt-2 break-all font-mono text-xs leading-5 text-gray-800">
        {highlightTags(query)}
      </p>
      {hint && <p className="mt-2 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
